import React, { useEffect, useCallback, useRef } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { Tooltip, Card, Button, Checkbox } from 'antd';
import clsx from 'clsx';
import { deepCopy, compareObjects } from 'lib';
import IconButton from 'component/icon/iconButton';
import Icon from 'component/icon';

const DraggableContent = (props) => {
    const { selector, onClickSelectorOption, storeColumns } = props;

    const onDragEnd = useCallback((result) => {
        if (!result.destination) {
            return;
        }
        const reorder = (list, startIndex, endIndex) => {
            const newList = deepCopy(list);
            const [removed] = newList.splice(startIndex, 1);
            newList.splice(endIndex, 0, removed);
            return newList;
        };
        const newColumns = reorder(selector.columns, result.source.index, result.destination.index);
        selector.setColumns(newColumns);
        storeColumns(selector.key, newColumns);
    }, [selector, storeColumns]);

    return (
        <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="droppable">
                {(dropProvided) => (
                    <div
                        {...dropProvided.droppableProps}
                        ref={dropProvided.innerRef}
                    >
                        {selector.columns?.map((col, index) => (
                            <Draggable
                                key={col.dataIndex}
                                draggableId={col.dataIndex}
                                index={index}
                            >
                                {(provided) => (
                                    <div
                                        ref={provided.innerRef}
                                        {...provided.draggableProps}
                                        className="m-0 mb-3"
                                    >
                                        <span
                                            {...provided.dragHandleProps}
                                        >
                                            <Icon name="menu" className="mr-1 text-gray-dark02" />
                                        </span>
                                        <Checkbox
                                            className="ml-2"
                                            checked={col.isShow !== false}
                                            onChange={() => onClickSelectorOption(selector, col.dataIndex)}
                                        >
                                            {col.rawTitle || col.title}
                                        </Checkbox>
                                    </div>
                                )}
                            </Draggable>
                        ))}
                        {dropProvided.placeholder}
                    </div>
                )}
            </Droppable>
        </DragDropContext>
    );
};

const ColumnSelector = (props) => {
    const { selectors, icon, sortKey = 'sequence' } = props;

    const storeColumns = useCallback((key, newColumns) => {
        let newStoredColumns = newColumns.map((col) => ({
            dataIndex: col.dataIndex,
            isShow: col.isShow !== false
        }));
        if (key) {
            const storedColumnsString = localStorage.getItem(`table_${key}`);
            if (storedColumnsString) {
                const storedColumns = JSON.parse(storedColumnsString);
                newStoredColumns = newStoredColumns.map((col) => {
                    const oldCol = storedColumns.find((x) => x.dataIndex === col.dataIndex);
                    return { ...oldCol, ...col };
                });
            }
            localStorage.setItem(`table_${key}`, JSON.stringify(newStoredColumns));
        }
    }, []);
    const onClickReset = () => {
        selectors.forEach((selector) => {
            if (selector.isShow === false) {
                return;
            }
            let newColumns = [];
            if (selector.default) {
                newColumns = selector.default.map((col) => ({ ...col }));
            } else {
                newColumns = selector.columns.map((col) => ({ ...col, isShow: true }));
                if (selector.sortKey) {
                    newColumns.sort(compareObjects(selector.sortKey));
                } else if (sortKey) {
                    newColumns.sort(compareObjects(sortKey));
                }
            }
            selector.setColumns(newColumns);
            localStorage.removeItem(`table_${selector.key}`);
        });
    };
    const onClickSelectorOption = (selector, dataIndex) => {
        const newColumns = deepCopy(selector.columns);
        const findCol = newColumns.find((col) => col.dataIndex === dataIndex);
        findCol.isShow = findCol.isShow === false;
        selector.setColumns(newColumns);
        storeColumns(selector.key, newColumns);
    };

    const init = useRef(false);
    useEffect(() => {
        if (init.current) {
            return;
        }
        if (selectors?.length) {
            selectors?.forEach((selector) => {
                const storedColumnsString = localStorage.getItem(`table_${selector.key}`);
                if (storedColumnsString) {
                    const storedColumns = JSON.parse(storedColumnsString);
                    const newColumns = storedColumns.map((col) => {
                        const findCol = selector.columns.find((x) => x.dataIndex === col.dataIndex);
                        return { ...findCol, isShow: col.isShow };
                    });
                    selector.setColumns(newColumns);
                } else if (selector.default) {
                    selector.setColumns(selector.default);
                }
            });
            init.current = true;
        }
    }, [selectors]);

    return !!selectors?.length && (
        <Tooltip
            destroyTooltipOnHide={{ keepParent: false }}
            trigger="click"
            placement="bottomRight"
            overlayClassName="table-tooltip"
            title={(
                <Card>
                    <div className="flex justify-between items-center py-1 px-2">
                        <span className="mr-3 text-gray-dark01">
                            請勾選要顯示的表格欄位
                        </span>
                        <Button
                            className="flex items-center text-table-tab-selected border-table-tab-selected"
                            onClick={onClickReset}
                        >
                            恢復預設值
                        </Button>
                    </div>
                    <div className="table-tooltip-divider mt-2" />
                    <div className="flex">
                        {selectors.map((item, index) => item.isShow !== false && (
                            <div
                                key={item.title}
                                className={clsx(
                                    'pl-2',
                                    { 'w-full': selectors.length === 1 },
                                    { 'border-l border-gray': index !== 0 }
                                )}
                            >
                                <div className="mt-2 mb-3 font-bold text-table-tab-selected">
                                    {selectors.length > 1 && item.title}
                                </div>
                                <div className="max-h-[30vh] overflow-y-auto">
                                    <DraggableContent
                                        selector={item}
                                        onClickSelectorOption={onClickSelectorOption}
                                        storeColumns={storeColumns}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                </Card>
            )}
        >
            <IconButton icon={icon || <Icon name="menu" />} title="欄位設定" />
        </Tooltip>
    );
};

export default ColumnSelector;
